import { useState, useEffect, useRef } from 'react'
import { PrismBanner, PrismButton, PrismDialog, PrismInputText } from '@prism-ui/react'
import axios from 'axios'

interface EmailUrl {
  id: number
  name: string
  url: string
}

interface AddUrlModalProps {
  show: boolean
  onHide: () => void
  onUrlCreated: (newUrl: EmailUrl) => void
  editId?: number
  editName?: string
  editUrl?: string
}

const AddUrlModal = ({ show, onHide, onUrlCreated, editId, editName = '', editUrl = '' }: AddUrlModalProps) => {
  const [name, setName] = useState(editName)
  const [url, setUrl] = useState(editUrl)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  // PrismDialog fires `prismClose` whenever `open` flips to false — including when we close
  // it ourselves after a successful save. Without this flag the dismiss cleanup would run a
  // second time and call `onHide` again.
  const closingRef = useRef(false)

  // Update local state when props change
  useEffect(() => {
    setName(editName)
    setUrl(editUrl)
  }, [editName, editUrl])

  const closeModal = () => {
    closingRef.current = true
    setName('')
    setUrl('')
    setError('')
    onHide()
  }

  // Only run the dismiss cleanup for a genuine dismissal (X / Escape / backdrop), not for a
  // close we initiated ourselves.
  const handleDialogClose = () => {
    if (closingRef.current) {
      closingRef.current = false
      return
    }
    closeModal()
    closingRef.current = false
  }

  const handleSubmit = async () => {
    if (saving) return
    setError('')

    setSaving(true)
    try {
      let response
      if (editId) {
        response = await axios.put(`/api/email_urls/${editId}`, { name: name.trim(), url: url.trim() })
      } else {
        response = await axios.post('/api/email_urls', { name: name.trim(), url: url.trim() })
      }
      const newUrl = response.data
      closeModal()
      onUrlCreated(newUrl)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } }
      setError(axiosError.response?.data?.error || 'Failed to save URL')
    } finally {
      setSaving(false)
    }
  }

  // The native <form> submit the Bootstrap version relied on is gone, so Enter is wired up
  // explicitly to keep keyboard submission working.
  const handleKeydown = (e: CustomEvent<{ key?: string }>) => {
    if (e.detail?.key === 'Enter') handleSubmit()
  }

  return (
    <PrismDialog
      open={show}
      setAriaLabel={editId ? 'Edit URL' : 'Add URL'}
      onPrismClose={handleDialogClose}
    >
      <span slot="header">{editId ? 'Edit URL' : 'Add URL'}</span>
      <div className="email-url-dialog-body">
        {error ? <PrismBanner tone="danger">{error}</PrismBanner> : null}
        <PrismInputText
          label="Name"
          value={name}
          placeholder="e.g., Manager Approval Link"
          onPrismInput={(e: CustomEvent<{ value: string }>) => setName(e.detail?.value || '')}
          onPrismKeydown={handleKeydown}
        />
        <PrismInputText
          label="URL"
          type="url"
          value={url}
          placeholder="https://..."
          onPrismInput={(e: CustomEvent<{ value: string }>) => setUrl(e.detail?.value || '')}
          onPrismKeydown={handleKeydown}
        />
      </div>

      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={closeModal}>
        Cancel
      </PrismButton>
      <PrismButton
        slot="footer"
        tone="primary"
        display="fill"
        disabled={saving}
        loading={saving}
        onPrismClick={handleSubmit}
      >
        <span>{editId ? 'Update' : 'Add'}</span>
      </PrismButton>
    </PrismDialog>
  )
}

export default AddUrlModal
